import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4lang_es_ES from '@amcharts/amcharts4/lang/es_ES';
import { NgStyle, PercentPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ITooltip } from '@bp-core/src/lib/models/portal/goal/tooltip.model';
import { environment } from '@environment/environment';
import { fuseAnimations } from '@fuse/animations';
import dayjs from 'dayjs';
import moment from 'moment';
import { IChart } from 'projects/bp-core/src/lib/models/portal/chart.model';
import am4themes_betterplan from 'projects/bp-portal/src/bp/shared/chart/betterplan-theme-amchart';
import { TooltipChartComponent } from 'projects/bp-portal/src/bp/shared/components/tooltip-chart/tooltip-chart.component';
import { TooltipChartComponent as TooltipChartComponent_1 } from '../../../tooltip-chart/tooltip-chart.component';

am4core.addLicense(environment.amchartLicence);
am4core.useTheme(am4themes_betterplan);
am4core.options.autoDispose = false;

@Component({
  selector: 'app-g-p-p-e-chart',
  templateUrl: './g-p-p-e-chart.component.html',
  styleUrls: ['./g-p-p-e-chart.component.scss'],
  providers: [PercentPipe],
  animations: fuseAnimations,
  standalone: true,
  imports: [NgStyle, TooltipChartComponent_1],
})
export class GPPEChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  constructor(
    private zone: NgZone,
    private cd: ChangeDetectorRef,
    private percentPipe: PercentPipe,
  ) {}

  private chart: am4charts.XYChart;
  @Input() profitabilities: IChart;
  @Input() chartClass = 'w-full h-72 md:h-96';
  emptyChart = false;

  @ViewChild('chartdivperformanceevolution') chartdivperformanceevolution: ElementRef;
  @ViewChild('tool') tool: TooltipChartComponent;

  firsValuetDate = '';
  lastValuetDate = '';
  datesWidth = '90%';

  tooltip: ITooltip = {
    title: '',
    values: [],
  };

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    // Si el chart ya existe y llegaron datos, actualizamos
    if (this.chart && this.profitabilities) {
      this.updateChartData();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        // Creación y configuración inicial del chart
        this.chart = am4core.create(
          this.chartdivperformanceevolution.nativeElement,
          am4charts.XYChart,
        );
        this.chart.tapToActivate = true;
        this.chart.paddingRight = 0;
        this.chart.paddingLeft = 0;
        this.chart.responsive.enabled = true;
        this.chart.responsive.useDefault = false;

        // Eje X (fecha)
        const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.rangeChangeDuration = 0; // <-- desactiva animación de rango
        dateAxis.rangeChangeEasing = am4core.ease.linear;

        const axisTooltip = dateAxis.tooltip!;
        axisTooltip.background.fill = am4core.color('rgba(0, 0, 0, 0)');
        axisTooltip.background.strokeWidth = 0;
        axisTooltip.ignoreBounds = true;
        axisTooltip.background.pointerLength = 0;
        axisTooltip.dy = -24;
        axisTooltip.html = '<h3 style="color:black">&#8226;</h3>';
        dateAxis.dateFormats.setKey('day', 'dd');
        dateAxis.periodChangeDateFormats.setKey('month', '[bold]yyyy[/]');
        dateAxis.periodChangeDateFormats.setKey('day', '[bold]MMM[/]');
        dateAxis.renderer.minGridDistance = 40;
        dateAxis.renderer.grid.template.disabled = true;
        dateAxis.renderer.line.strokeOpacity = 1;
        dateAxis.renderer.line.strokeWidth = 0.5;
        dateAxis.renderer.line.stroke = am4core.color('#8d949d');
        dateAxis.renderer.labels.template.opacity = 0;

        // Eje Y (porcentaje)
        const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.rangeChangeDuration = 0; // <-- desactiva animación de rango
        valueAxis.rangeChangeEasing = am4core.ease.linear;
        valueAxis.renderer.grid.template.disabled = false;
        valueAxis.renderer.grid.template.stroke = am4core.color('#d9d9d9');
        valueAxis.renderer.grid.template.strokeWidth = 0.7;
        valueAxis.renderer.grid.template.strokeOpacity = 0.7;
        valueAxis.renderer.line.strokeOpacity = 0;
        valueAxis.renderer.baseGrid.disabled = true;
        valueAxis.cursorTooltipEnabled = false;
        valueAxis.renderer.opposite = true;
        valueAxis.renderer.line.strokeWidth = 0.5;
        valueAxis.renderer.line.stroke = am4core.color('#8d949d');
        valueAxis.renderer.padding(0, 0, 0, 0);
        valueAxis.renderer.margin(0, 0, 0, 0);
        valueAxis.renderer.labels.template.fill = am4core.color('#8d949d');
        valueAxis.renderer.labels.template.horizontalCenter = 'right';
        valueAxis.renderer.labels.template.align = 'right';
        this.chart.paddingRight = -10;
        valueAxis.numberFormatter.numberFormat = "#'%'";

        // Línea base 0%
        const zeroGuide = valueAxis.axisRanges.create();
        zeroGuide.value = 0;
        zeroGuide.grid.stroke = am4core.color('#d9d9d9');
        zeroGuide.grid.strokeWidth = 2.0;
        zeroGuide.grid.strokeOpacity = 1;

        // Cursor
        this.chart.cursor = new am4charts.XYCursor();
        this.chart.cursor.xAxis = dateAxis;
        this.chart.cursor.lineY.disabled = true;

        // Idioma
        this.chart.language.locale = am4lang_es_ES;

        // Evento de zoom para actualizar fechas
        dateAxis.events.on('startchanged', e => {
          this.firsValuetDate = dayjs(e.target.minZoomed).format('MMM DD, YYYY');
          this.lastValuetDate = dayjs(e.target.maxZoomed).format('MMM DD, YYYY');
        });

        // Si ya tenemos datos de entrada, pintamos
        this.chart.seriesContainer.defaultState.transitionDuration = 0;
        this.chart.seriesContainer.hiddenState.transitionDuration = 0;
        if (this.profitabilities) {
          this.updateChartData();
        }
      });
    }, 200);
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }

  /**
   * Actualiza series, datos y tooltip del chart.
   */
  private updateChartData(): void {
    // Detectar si no hay data
    this.detectEmptyChart();

    // Limpiar series anteriores
    this.chart.series.clear();

    // Construcción de cada serie y acumulación de HTML para tooltip
    let tooltipHtml = `<strong>{date.formatDate('dd-MM-yyyy')}</strong><table>`;
    this.profitabilities.values.forEach(value => {
      const series = this.chart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = 'date';
      series.dataFields.valueY = value.key?.trim();
      series.stroke = am4core.color(value.color);
      series.fill = am4core.color(value.color);
      series.name = value.portfolioTitle ?? ' ';
      series.strokeOpacity = 1;
      series.strokeWidth = 1.5;

      const title =
        (value.portfolioTitle ? value.portfolioTitle : value.title) +
        ' ' +
        (value.currencyCode === '--' ? '' : value.currencyCode);

      tooltipHtml += `
        <tr class="w-100">
          <td>
            <span class="dot-chart" style="background-color:${value.color}; margin-right:2px"></span>
          </td>
          <td class="text-left">${title}: {${value.key?.trim()}}</td>
        </tr>
      `;
    });
    tooltipHtml += '</table>';

    // Asignar datos al chart
    this.chart.data = this.profitabilities.data;

    // Fechas de primer y último punto
    const dataArr = this.profitabilities.data;
    if (dataArr?.length) {
      this.firsValuetDate = dayjs(dataArr[0].date).format('MMM DD, YYYY');
      this.lastValuetDate = dayjs(dataArr[dataArr.length - 1].date).format('MMM DD, YYYY');
    }

    // Configurar tooltip visual
    if (this.chart.series.length > 0 && this.chart.series.values[0].tooltip) {
      const tt = this.chart.series.values[0].tooltip;
      tt.getFillFromObject = false;
      tt.background.fill = am4core.color('#FFFFFF');
      tt.autoTextColor = false;
      tt.label.fill = am4core.color('black');
    }
    this.addTooltipHtml(this.chart.series.values[0] as am4charts.LineSeries);

    // Ajustar fuentes y zoom según tamaño
    this.onResize();
  }

  /**
   * Inyecta el HTML personalizado en el tooltip nativo de amCharts.
   */
  private addTooltipHtml(lineSeries: am4charts.LineSeries) {
    lineSeries.adapter.add('tooltipHTML', (_html, target) => {
      const data = target.tooltipDataItem.dataContext as any;
      if (!data) {
        return '';
      }
      // 1️⃣ Preparo un array con label, color, signo y valor formateado
      const items = this.profitabilities.values.map(p => {
        const label =
          (p.portfolioTitle || p.title) + (p.currencyCode === '--' ? '' : ` ${p.currencyCode}`);
        const raw = data[p.key!];
        const sign = raw > 0 ? '+' : '';
        const formatted = raw != null ? this.percentPipe.transform(raw / 100, '1.1-2') : '';
        return { label, color: p.color, sign, value: formatted };
      });

      // 2️⃣ Empiezo a montar el HTML puro
      let html = `<div>
      <p class="text-secondary">
        ${moment(data.date).format('DD MMM yyyy')}
      </p>
      <div class="flex flex-row">
        <div class="flex flex-col">
    `;

      // 3️⃣ Primera columna: punto + label
      for (const it of items) {
        const dotClass = it.label === 'Depósitos' ? 'w-2 h-1 mr-3' : 'w-2 h-2 rounded-xl mr-3';
        html += `
        <div class="flex items-center">
          <div
            class="${dotClass}"
            style="background-color: ${it.color}"
          ></div>
          ${it.label}
        </div>
      `;
      }

      // 4️⃣ Segunda columna: signo + valor
      html += `</div><div class="flex flex-col">`;
      for (const it of items) {
        html += `
        <div class="ml-3 text-right">
          ${it.sign}${it.value}
        </div>
      `;
      }

      // 5️⃣ Cierro tags
      html += `</div></div></div>`;

      return html;
    });
  }

  /**
   * Determina si el chart está vacío y muestra mensaje.
   */
  detectEmptyChart(): void {
    this.emptyChart = this.profitabilities.data.length === 0 && this.chart.data.length <= 1;
    const emptyLabel = this.chart.createChild(am4core.Label);

    if (this.emptyChart) {
      emptyLabel.text = `[bold font-size:15x] Acá observarás la rentabilidad en el tiempo`;
      emptyLabel.x = am4core.percent(50);
      emptyLabel.y = am4core.percent(50);
      emptyLabel.align = 'center';
      emptyLabel.wrap = true;
      emptyLabel.horizontalCenter = 'middle';
      emptyLabel.isMeasured = false;
    } else {
      emptyLabel.dispose();
    }
  }

  /**
   * Ajusta tamaño de fuentes y comportamiento del cursor al redimensionar ventana.
   */
  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    const screenWidth = event ? event.target.innerWidth : window.innerWidth;
    if (!this.chart) {
      return;
    }
    if (screenWidth < 576) {
      this.chart.fontSize = 9;
      this.chart.cursor.behavior = 'none';
      this.chart.zoomOutButton.disabled = true;
    } else if (screenWidth < 768) {
      this.chart.fontSize = 9;
      this.chart.cursor.behavior = 'zoomX';
      this.chart.zoomOutButton.disabled = false;
    } else {
      this.chart.fontSize = 12;
      this.chart.cursor.behavior = 'zoomX';
      this.chart.zoomOutButton.disabled = false;
    }

    // Ajuste de ancho de fechas después de render
    setTimeout(() => {
      const mb = this.chart.axisBulletsContainer?.maxRight?.toString() + 'px';
      if (mb && this.datesWidth !== mb) {
        this.datesWidth = mb;
        this.cd.detectChanges();
      }
    }, 50);
  }
}
